import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material'
import { customerSupportAdminService } from '../../services'

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'export.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function filenameFromDisposition(header) {
  if (!header) return null
  const m = String(header).match(/filename="?([^"]+)"?/i)
  return m?.[1] || null
}

/**
 * Per-page export dialog: sections (optional) + column checkboxes.
 * kind: 'audit' | 'reports' | 'price_rca'
 */
export default function ExportDialog({
  open,
  onClose,
  kind,
  /** Params matching the current page filters (shop, dates, etc.) */
  requestParams = {},
  onError,
  onInfo,
}) {
  const [loadingCatalog, setLoadingCatalog] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [catalogEntry, setCatalogEntry] = useState(null)
  const [selectedSections, setSelectedSections] = useState({})
  const [selectedColumns, setSelectedColumns] = useState({})

  useEffect(() => {
    if (!open || !kind) return
    let cancelled = false
    ;(async () => {
      setLoadingCatalog(true)
      try {
        const catalog = await customerSupportAdminService.getExportCatalog()
        if (cancelled) return
        const entry = catalog?.[kind] || null
        setCatalogEntry(entry)
        if (entry?.columns) {
          const cols = {}
          entry.columns.forEach((c) => {
            cols[c.key] = c.defaultOn !== false
          })
          setSelectedColumns(cols)
          setSelectedSections({})
        } else if (entry?.sections) {
          const secs = {}
          const cols = {}
          entry.sections.forEach((s) => {
            secs[s.sectionId] = true
            cols[s.sectionId] = {}
            s.columns.forEach((c) => {
              cols[s.sectionId][c.key] = c.defaultOn !== false
            })
          })
          setSelectedSections(secs)
          setSelectedColumns(cols)
        }
      } catch (err) {
        onError?.(
          err?.response?.data?.message ||
            err.message ||
            'Failed to load export options',
        )
      } finally {
        if (!cancelled) setLoadingCatalog(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, kind, onError])

  const canSubmit = useMemo(() => {
    if (!catalogEntry) return false
    if (catalogEntry.columns) {
      return Object.values(selectedColumns).some(Boolean)
    }
    const activeSections = Object.entries(selectedSections)
      .filter(([, on]) => on)
      .map(([id]) => id)
    if (!activeSections.length) return false
    return activeSections.every((id) =>
      Object.values(selectedColumns[id] || {}).some(Boolean),
    )
  }, [catalogEntry, selectedColumns, selectedSections])

  const toggleSection = (sectionId, checked) => {
    setSelectedSections((prev) => ({ ...prev, [sectionId]: checked }))
  }

  const toggleFlatColumn = (key, checked) => {
    setSelectedColumns((prev) => ({ ...prev, [key]: checked }))
  }

  const toggleSectionColumn = (sectionId, key, checked) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: checked },
    }))
  }

  const setAllFlat = (on) => {
    if (!catalogEntry?.columns) return
    const next = {}
    catalogEntry.columns.forEach((c) => {
      next[c.key] = on
    })
    setSelectedColumns(next)
  }

  const runExport = async () => {
    setExporting(true)
    onError?.('')
    try {
      let response
      if (kind === 'audit') {
        const columns = Object.entries(selectedColumns)
          .filter(([, on]) => on)
          .map(([k]) => k)
        response = await customerSupportAdminService.exportAudit({
          ...requestParams,
          columns,
        })
      } else if (kind === 'reports') {
        const sections = Object.entries(selectedSections)
          .filter(([, on]) => on)
          .map(([id]) => id)
        const columnsBySection = {}
        sections.forEach((id) => {
          columnsBySection[id] = Object.entries(selectedColumns[id] || {})
            .filter(([, on]) => on)
            .map(([k]) => k)
        })
        response = await customerSupportAdminService.exportReports({
          ...requestParams,
          sections,
          columnsBySection,
        })
      } else if (kind === 'price_rca') {
        const sections = Object.entries(selectedSections)
          .filter(([, on]) => on)
          .map(([id]) => id)
        const columnsBySection = {}
        sections.forEach((id) => {
          columnsBySection[id] = Object.entries(selectedColumns[id] || {})
            .filter(([, on]) => on)
            .map(([k]) => k)
        })
        response = await customerSupportAdminService.exportPriceRca({
          ...requestParams,
          sections,
          columnsBySection,
        })
      } else {
        throw new Error('Unknown export type')
      }

      const blob = response.data
      const filename =
        filenameFromDisposition(response.headers?.['content-disposition']) ||
        `cs-${kind}-export.csv`
      downloadBlob(blob, filename)
      onInfo?.(`Export downloaded (${filename})`)
      onClose?.()
    } catch (err) {
      let message =
        err?.response?.data?.message || err.message || 'Export failed'
      // Blob error bodies from Nest
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          const parsed = JSON.parse(text)
          message = parsed.message || message
        } catch {
          /* keep */
        }
      }
      onError?.(Array.isArray(message) ? message.join(', ') : message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {catalogEntry?.title || 'Export'}
        {catalogEntry?.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {catalogEntry.description}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {loadingCatalog ? (
          <Typography variant="body2">Loading options…</Typography>
        ) : !catalogEntry ? (
          <Alert severity="warning">Export catalog unavailable.</Alert>
        ) : catalogEntry.columns ? (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button size="small" onClick={() => setAllFlat(true)}>
                Select all
              </Button>
              <Button size="small" onClick={() => setAllFlat(false)}>
                Clear all
              </Button>
            </Stack>
            <FormGroup>
              {catalogEntry.columns.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={
                    <Checkbox
                      checked={!!selectedColumns[c.key]}
                      onChange={(e) =>
                        toggleFlatColumn(c.key, e.target.checked)
                      }
                    />
                  }
                  label={c.label}
                />
              ))}
            </FormGroup>
          </Box>
        ) : (
          <Stack spacing={2}>
            {(catalogEntry.sections || []).map((section) => (
              <Box
                key={section.sectionId}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!selectedSections[section.sectionId]}
                      onChange={(e) =>
                        toggleSection(section.sectionId, e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography fontWeight={700}>{section.title}</Typography>
                  }
                />
                {selectedSections[section.sectionId] ? (
                  <FormGroup sx={{ pl: 3 }}>
                    {section.columns.map((c) => (
                      <FormControlLabel
                        key={c.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              !!selectedColumns[section.sectionId]?.[c.key]
                            }
                            onChange={(e) =>
                              toggleSectionColumn(
                                section.sectionId,
                                c.key,
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={c.label}
                      />
                    ))}
                  </FormGroup>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
        <Alert severity="info" sx={{ mt: 2 }}>
          Export uses the filters currently set on this page and includes all
          matching rows (up to 2,000 per section), not only the visible page.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || exporting || loadingCatalog}
          onClick={() => void runExport()}
        >
          {exporting ? 'Exporting…' : 'Download CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
